// The image models a person can pick for painting: new looks and shots. Shared by the server (which routes each
// request to its model, see src/server/paint.ts) and the picker in the browser. Chosen from a test of eight models on
// real shots, 18 Oct 2026 (archive/experiments/scripts/model-test.ts). Costs stay out of the app.
export const IMAGE_MODELS = [
  { id: "nb-lite", name: "Nano Banana Lite", hint: "Fastest, done in seconds" },
  { id: "muse", name: "Muse", hint: "Lovely style, but refuses some characters" },
  { id: "gpt", name: "GPT Image 2.5 Flare", hint: "Best at camera angles and poses" },
  { id: "gpt-sunburst", name: "GPT Image 2.5 Sunburst", hint: "The other GPT Image 2.5, to compare with Flare" },
  { id: "nb21", name: "Nano Banana 2.1", hint: "Thinks the picture through" },
  { id: "ideogram", name: "Ideogram 4.5", hint: "Edits a picture and keeps the rest untouched" },
] as const;

export type ImageModel = (typeof IMAGE_MODELS)[number]["id"];
export const DEFAULT_IMAGE_MODEL: ImageModel = "gpt"; // on low quality (qualityFor's default)
export const isImageModel = (v: unknown): v is ImageModel => IMAGE_MODELS.some(m => m.id === v);
export const imageModelName = (id: ImageModel) => IMAGE_MODELS.find(m => m.id === id)!.name;

// Quality: only some models have a setting for it. GPT Image paints at low, medium or high quality. Nano Banana 2.1
// paints at 1K, 2K or 4K (low, medium, high). Lite and Muse have one quality. Low is what each model did before.
// Ideogram has two settings: quality (very low to high) and edit precision (regular, or high, which puts back every
// pixel the edit shouldn't have touched). Both travel in the one `quality` field of a request as "tier:precision",
// e.g. "very_low:high", so only this file and the picker need to know.
export const QUALITIES = ["low", "medium", "high"] as const;
export type ImageQuality = (typeof QUALITIES)[number];
export const QUALITY_NAMES: Record<ImageQuality, string> = { low: "Low", medium: "Medium", high: "High" };
export const IDEOGRAM_TIERS = ["very_low", "low", "medium", "high"] as const;
export const IDEOGRAM_TIER_NAMES: Record<(typeof IDEOGRAM_TIERS)[number], string> = { very_low: "Very low", low: "Low", medium: "Medium", high: "High" };
export const IDEOGRAM_PRECISIONS = ["regular", "high"] as const;
export const IDEOGRAM_PRECISION_NAMES: Record<(typeof IDEOGRAM_PRECISIONS)[number], string> = { regular: "Regular", high: "High" };
export type IdeogramTier = (typeof IDEOGRAM_TIERS)[number];
export type IdeogramPrecision = (typeof IDEOGRAM_PRECISIONS)[number];
export type IdeogramSetting = `${IdeogramTier}:${IdeogramPrecision}`;
export type PaintQuality = ImageQuality | IdeogramSetting;
export const DEFAULT_IDEOGRAM: IdeogramSetting = "very_low:high";
export const ideogramSetting = (tier: IdeogramTier, precision: IdeogramPrecision): IdeogramSetting => `${tier}:${precision}`;
export function parseIdeogram(q: unknown): { tier: IdeogramTier; precision: IdeogramPrecision } {
  const [tier, precision] = typeof q === "string" ? q.split(":") : [];
  const d = DEFAULT_IDEOGRAM.split(":");
  return {
    tier: (IDEOGRAM_TIERS as readonly string[]).includes(tier!) ? (tier as IdeogramTier) : (d[0] as IdeogramTier),
    precision: (IDEOGRAM_PRECISIONS as readonly string[]).includes(precision!) ? (precision as IdeogramPrecision) : (d[1] as IdeogramPrecision),
  };
}
const QUALITY_MODELS: readonly ImageModel[] = ["gpt", "gpt-sunburst", "nb21", "ideogram"];
export const hasQuality = (m: ImageModel) => QUALITY_MODELS.includes(m);
export const isQuality = (v: unknown): v is ImageQuality => QUALITIES.includes(v as ImageQuality);
// The quality to paint at: the one asked for if this model has the setting and it's valid, else the model's default.
export function qualityFor(m: ImageModel, q: unknown): PaintQuality {
  if (m === "ideogram") { const { tier, precision } = parseIdeogram(q); return ideogramSetting(tier, precision); }
  return hasQuality(m) && isQuality(q) ? q : "low";
}
